/**
 * ProCarerProfile — shared Carer Profile editor (v4, member-facing).
 *
 * Free to create, edit, publish and keep visible for EVERY registered
 * JCC Carer (Step 3, 2026-08-24) — one form, one route, one database
 * record, serving all three carer tiers identically. As of the Step 3
 * amendment (2026-08-24), ALL fields (including qualifications,
 * testimonials, indicative rates, languages, DBS/insurance detail,
 * driving/vehicle and detailed working preferences) are available to
 * every carer tier — paid membership must never produce a richer or
 * more persuasive public profile. There is no locked/Pro-only section
 * any more.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Camera, X, Loader2, Send, CheckCircle2, AlertTriangle,
  Eye, EyeOff, Share2, Copy, Check,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { CarerReviewNotice } from "../components/shared/CarerReviewNotice";
import { PrivacyReassuranceNote } from "../components/shared/PrivacyReassuranceNote";
import { isCarerReviewPending } from "../lib/carerReview";

const CARE_TYPES = ["Live-in Care", "Dementia Care", "Mobility Support", "Personal Care", "Couples Care", "End-of-life care", "Respite care", "Hourly Care"];
const TRAVEL_OPTIONS = ["Yes - UK wide", "Yes - limited regions", "No - local only"];
const DBS_OPTIONS = ["Enhanced DBS (current)", "Enhanced DBS (update service)", "Basic DBS", "Applied / in progress", "Not currently held"];
const INSURANCE_OPTIONS = ["Public Liability & Professional Indemnity", "Public Liability only", "In progress", "Not currently held"];
const CONTACT_METHODS = ["Email", "Phone call", "Whatsapp", "Text message (SMS)", "Email First then call"];
const AVAILABILITY_OPTIONS = ["Available now", "Available from a date", "Limited availability", "Not currently available"];
const DBS_UPDATE_SERVICE_OPTIONS = ["Yes", "No", "Not sure"];

// Core fields — sent on every save. Mirrors CONTENT_FIELDS in
// care_profiles.py (minus EXTENDED_FIELD_NAMES below).
const CORE_FIELD_NAMES = [
  "about_you", "general_location", "care_types", "care_experience_description",
  "willing_to_travel", "preferred_work_pattern", "availability_status", "earliest_start_date",
  "dbs_status", "insurance_status", "preferred_contact_methods", "declaration_consent",
];
// Extended fields — available to every carer tier (Step 3 amendment,
// 2026-08-24). Mirrors EXTENDED_PROFILE_FIELDS in care_profiles.py.
const EXTENDED_FIELD_NAMES = [
  "qualifications_training", "additional_specialisms", "detailed_rota_preferences",
  "has_driving_licence", "has_vehicle", "languages_spoken",
  "dbs_certificate_date", "dbs_update_service",
  "insurance_type", "insurance_renewal_date",
  "testimonials", "indicative_rates", "travel_radius_detail", "professional_background",
];

// Required-to-submit fields — deliberately core-only (mirrors
// REQUIRED_FIELD_CHECKS in care_profiles.py); none of the extended
// fields above are required to submit.
const REQUIRED_FIELDS = [
  ["about_you", "About you", (p) => !!(p.about_you || "").trim()],
  ["care_types", "At least one care type", (p) => !!(p.care_types || []).length],
  ["care_experience_description", "Care experience description", (p) => !!(p.care_experience_description || "").trim()],
  ["willing_to_travel", "Willing to travel", (p) => !!p.willing_to_travel],
  ["dbs_status", "DBS status", (p) => !!p.dbs_status],
  ["insurance_status", "Insurance status", (p) => !!p.insurance_status],
  ["photo", "Profile photo", (p) => !!p.photo_data_url],
  ["declaration_consent", "Accuracy declaration", (p) => !!p.declaration_consent],
];
function missingFieldsFor(profile) {
  return REQUIRED_FIELDS.filter(([, , check]) => !check(profile)).map(([, label]) => label);
}

function SectionCard({ eyebrow, title, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 space-y-5">
      <div>
        <div className="overline text-purple">{eyebrow}</div>
        <h2 className="mt-1 font-display font-semibold text-lg">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function PillToggle({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
        active ? "bg-purple text-white border-purple" : "border-slate-200 text-ink-muted hover:border-purple/40"
      }`}
    >
      {label}
    </button>
  );
}

function RequiredMark({ missing }) {
  if (!missing) return null;
  return (
    <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-amber-600" data-testid="required-mark">
      Required
    </span>
  );
}

function TierBadge({ tier, badge }) {
  if (!badge) return null;
  const cls = tier === "pro_carer" ? "bg-purple/10 text-purple" : "bg-slate-100 text-ink";
  return <span className={`pill text-xs font-semibold ${cls}`} data-testid="profile-tier-badge">{badge}</span>;
}

export default function ProCarerProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewBlocked, setReviewBlocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [sharingSaving, setSharingSaving] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/carer-profile/mine");
        setProfile(data);
      } catch (e) {
        if (isCarerReviewPending(e)) {
          setReviewBlocked(true);
        } else {
          toast.error(formatApiError(e?.response?.data?.detail) || "Could not load your profile.");
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const isPro = !!profile?.is_pro;

  const set = (key) => (value) => { setProfile((p) => ({ ...p, [key]: value })); setDirty(true); };

  const toggleInArray = (key, value) => {
    setProfile((p) => {
      const arr = p[key] || [];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...p, [key]: next };
    });
    setDirty(true);
  };

  const buildPayload = (p) => {
    const payload = {};
    [...CORE_FIELD_NAMES, ...EXTENDED_FIELD_NAMES].forEach((k) => { payload[k] = p[k]; });
    return payload;
  };

  const saveDraft = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/api/carer-profile/mine", buildPayload(profile));
      setProfile(data);
      setDirty(false);
      setLastSaved(new Date());
      toast.success("Draft saved.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not save your draft.");
    } finally { setSaving(false); }
  };

  const toggleVisibility = async (next) => {
    setVisibilitySaving(true);
    try {
      const { data } = await api.patch("/api/carer-profile/mine", { publicly_visible: next });
      setProfile((p) => ({ ...p, ...data }));
      toast.success(next ? "Your profile is visible in the Carer directory." : "Your profile is hidden from the Carer directory.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update visibility.");
    } finally { setVisibilitySaving(false); }
  };

  const togglePublicSharing = async (next) => {
    setSharingSaving(true);
    try {
      const { data } = await api.post("/api/carer-profile/mine/public-sharing", { enabled: next });
      setProfile((p) => ({ ...p, ...data }));
      if (next) {
        toast.success(
          data.email_sent
            ? "Public Sharing is on — we've emailed you your link."
            : "Public Sharing is on. Copy your link below to share it."
        );
      } else {
        toast.success("Public Sharing is off — your link will no longer work.");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update Public Sharing.");
    } finally { setSharingSaving(false); }
  };

  const copyShareLink = async () => {
    const link = profile?.public_profile_link;
    if (!link) return;
    const fullLink = link.startsWith("http") ? link : `${window.location.origin}${link}`;
    try {
      await navigator.clipboard.writeText(fullLink);
      setLinkCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link — please select and copy it manually.");
    }
  };

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/api/carer-profile/mine/photo", form);
      // Merge only the photo fields from the server response — do NOT
      // replace the whole profile object, which would wipe out any
      // unsaved text-field edits the member made before uploading.
      setProfile((p) => ({
        ...p,
        photo_data_url: data.photo_data_url,
        photo_filename: data.photo_filename,
        updated_at: data.updated_at,
      }));
      toast.success("Photo uploaded.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not upload photo.");
    } finally { setUploadingPhoto(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const removePhoto = async () => {
    try {
      const { data } = await api.delete("/api/carer-profile/mine/photo");
      setProfile((p) => ({
        ...p,
        photo_data_url: data.photo_data_url,
        photo_filename: data.photo_filename,
        updated_at: data.updated_at,
      }));
      toast.success("Photo removed.");
    } catch (e) { toast.error("Could not remove photo."); }
  };

  const submitForReview = async () => {
    if (dirty) { await saveDraft(); }
    setSubmitting(true);
    try {
      const { data } = await api.post("/api/carer-profile/mine/submit");
      setProfile(data);
      toast.success("Your profile has been sent to JCC for review.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not submit your profile.");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-carer flex items-center justify-center" data-testid="page-pro-carer-profile-loading">
        <Loader2 className="w-6 h-6 animate-spin text-purple" />
      </div>
    );
  }
  if (reviewBlocked) {
    return (
      <div className="min-h-screen bg-surface-carer flex items-center justify-center px-4" data-testid="page-pro-carer-profile-review-blocked">
        <div className="max-w-md w-full">
          <CarerReviewNotice />
        </div>
      </div>
    );
  }
  if (!profile) return null;

  const liveMissing = missingFieldsFor(profile);
  const completed = REQUIRED_FIELDS.length - liveMissing.length;
  const isRequiredMissing = (label) => liveMissing.includes(label);
  const inputCls = "mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30";
  const backHref = isPro ? "/workspace/pro-carer-circle" : "/workspace/carer-starter";
  const backLabel = isPro ? "Back to Pro Carer Office hub" : "Back to Carer Starter hub";

  return (
    <div className="min-h-screen bg-surface-carer" data-testid="page-pro-carer-profile">
      <div className="container-jcc py-8 md:py-14 max-w-3xl">
        <Link to={backHref} className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="profile-back">
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </Link>

        <header className="mt-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="overline text-purple">Carer Profile</div>
              <TierBadge tier={profile.membership_tier} badge={profile.badge} />
              <span
                className={`pill text-xs ${
                  profile.status === "submitted" ? "bg-teal/10 text-teal"
                  : profile.status === "approved" ? "bg-teal/10 text-teal"
                  : profile.status === "changes_requested" ? "bg-amber-100 text-amber-800"
                  : "bg-slate-100 text-ink-muted"
                }`}
                data-testid="profile-status-pill"
              >
                {profile.status === "submitted" ? "Submitted — awaiting review"
                  : profile.status === "approved" ? "Approved"
                  : profile.status === "changes_requested" ? "Changes requested"
                  : "Draft"}
              </span>
            </div>
            <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl">Your Carer Profile</h1>
            <p className="mt-3 text-ink-muted max-w-2xl leading-relaxed">
              Save your details here as a draft — you can come back and continue at any time.
              This is your working draft only; nothing here is published or shared until JCC approves it.
            </p>
          </div>
        </header>

        {(profile.status === "draft" || profile.status === "changes_requested") && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="profile-completion-strip">
            <div className="flex-1 min-w-[200px]">
              <span className="text-sm font-medium text-ink" data-testid="profile-completion-count">
                {completed} of {REQUIRED_FIELDS.length} required items completed
              </span>
              <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-teal transition-all"
                  style={{ width: `${(completed / REQUIRED_FIELDS.length) * 100}%` }}
                />
              </div>
            </div>
            {liveMissing.length > 0 && (
              <p className="text-xs text-amber-700 max-w-xs" data-testid="profile-missing-fields">
                Still needed: {liveMissing.join(", ")}.
              </p>
            )}
          </div>
        )}

        {profile.status === "submitted" && (
          <div className="mt-6 rounded-2xl border border-teal/30 bg-teal/5 p-4 flex items-start gap-3" data-testid="profile-submitted-banner">
            <CheckCircle2 className="w-5 h-5 text-teal shrink-0 mt-0.5" />
            <p className="text-sm text-ink">
              Your profile has been sent to JCC for review. You can keep editing it below — JCC will
              be in touch once it's been reviewed.
            </p>
          </div>
        )}

        {profile.status === "approved" && (
          <div className="mt-6 rounded-2xl border border-teal/30 bg-teal/5 p-4 flex items-start gap-3" data-testid="profile-approved-banner">
            <CheckCircle2 className="w-5 h-5 text-teal shrink-0 mt-0.5" />
            <p className="text-sm text-ink">
              Your profile has been reviewed and approved by the JCC team. No action is needed from
              you right now.
            </p>
          </div>
        )}

        {profile.status === "changes_requested" && (
          <div className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="profile-changes-requested-banner">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900">JCC has requested a few changes</p>
              <p className="mt-1 text-sm text-amber-900 whitespace-pre-wrap" data-testid="profile-feedback-note">
                {profile.feedback_note}
              </p>
              <p className="mt-2 text-xs text-amber-800">
                Update your profile below, then submit it again for review.
              </p>
            </div>
          </div>
        )}

        <div className="mt-8 space-y-6">
          <SectionCard eyebrow="Photo" title="Profile photo">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                {profile.photo_data_url
                  ? <img src={profile.photo_data_url} alt="Your profile" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
                  : <Camera className="w-6 h-6 text-slate-400" />}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onPhotoChange} data-testid="profile-photo-input" />
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}
                        className="btn-outline text-sm w-fit" data-testid="profile-photo-upload-btn">
                  {uploadingPhoto ? "Uploading…" : profile.photo_data_url ? "Replace photo" : "Upload photo"}
                </button>
                {profile.photo_data_url && (
                  <button type="button" onClick={removePhoto} className="text-xs text-ink-muted hover:text-ink inline-flex items-center gap-1 w-fit" data-testid="profile-photo-remove-btn">
                    <X className="w-3 h-3" /> Remove photo
                  </button>
                )}
                <span className="text-xs text-ink-muted flex items-center">
                  Profile photo<RequiredMark missing={isRequiredMissing("Profile photo")} />
                </span>
              </div>
            </div>
          </SectionCard>

          <SectionCard eyebrow="About you" title="Tell us a little about you">
            <label className="block">
              <span className="text-sm font-medium text-ink flex items-center">About you<RequiredMark missing={isRequiredMissing("About you")} /></span>
              <textarea
                value={profile.about_you || ""} rows={4}
                onChange={(e) => set("about_you")(e.target.value)}
                placeholder="In your own words, tell families about your approach to care, what matters to you, and the kind of support you offer."
                className={inputCls} data-testid="profile-about-you"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-ink">General location</span>
              <input
                type="text"
                value={profile.general_location || ""}
                onChange={(e) => set("general_location")(e.target.value)}
                placeholder="e.g. Manchester, Greater Manchester — a town/city or general area, not your exact address"
                className={inputCls} data-testid="profile-general-location"
              />
              <span className="mt-1 block text-xs text-ink-muted">
                Broad location only — a town/city, county or general area. Please don&rsquo;t include your exact home address.
              </span>
            </label>
          </SectionCard>

          <SectionCard eyebrow="Care experience" title="What kind of care do you provide?">
            <div>
              <span className="text-sm font-medium text-ink flex items-center mb-2">At least one care type<RequiredMark missing={isRequiredMissing("At least one care type")} /></span>
              <div className="flex flex-wrap gap-2" data-testid="profile-care-types">
                {CARE_TYPES.map((opt) => (
                  <PillToggle key={opt} label={opt} active={(profile.care_types || []).includes(opt)}
                              onClick={() => toggleInArray("care_types", opt)} testid={`profile-care-type-${opt}`} />
                ))}
              </div>
            </div>
            <label className="block">
              <span className="text-sm font-medium text-ink flex items-center">Describe your care experience<RequiredMark missing={isRequiredMissing("Care experience description")} /></span>
              <textarea
                value={profile.care_experience_description || ""} rows={4}
                onChange={(e) => set("care_experience_description")(e.target.value)}
                className={inputCls} data-testid="profile-care-experience"
              />
            </label>
          </SectionCard>

          <SectionCard eyebrow="Availability & preferences" title="Travel, rota and availability">
            <div>
              <span className="text-sm font-medium text-ink flex items-center mb-2">Willing to travel<RequiredMark missing={isRequiredMissing("Willing to travel")} /></span>
              <div className="flex flex-wrap gap-2" data-testid="profile-willing-to-travel">
                {TRAVEL_OPTIONS.map((opt) => (
                  <PillToggle key={opt} label={opt} active={profile.willing_to_travel === opt}
                              onClick={() => set("willing_to_travel")(opt)} testid={`profile-travel-${opt}`} />
                ))}
              </div>
            </div>
            <label className="block">
              <span className="text-sm font-medium text-ink">Preferred work pattern / rota</span>
              <input type="text" value={profile.preferred_work_pattern || ""}
                     onChange={(e) => set("preferred_work_pattern")(e.target.value)}
                     placeholder="e.g. 3 weeks on / 2 weeks off"
                     className={inputCls} data-testid="profile-work-pattern" />
            </label>
            <div>
              <span className="text-sm font-medium text-ink block mb-2">Availability status</span>
              <div className="flex flex-wrap gap-2" data-testid="profile-availability-status">
                {AVAILABILITY_OPTIONS.map((opt) => (
                  <PillToggle key={opt} label={opt} active={profile.availability_status === opt}
                              onClick={() => set("availability_status")(opt)} testid={`profile-availability-${opt}`} />
                ))}
              </div>
            </div>
            {profile.availability_status === "Available from a date" && (
              <label className="block">
                <span className="text-sm font-medium text-ink">Earliest start date</span>
                <input type="date" value={profile.earliest_start_date || ""}
                       onChange={(e) => set("earliest_start_date")(e.target.value)}
                       className={inputCls} data-testid="profile-start-date" />
              </label>
            )}
          </SectionCard>

          <SectionCard eyebrow="Professional checks" title="DBS and insurance">
            <div>
              <span className="text-sm font-medium text-ink flex items-center mb-2">DBS status<RequiredMark missing={isRequiredMissing("DBS status")} /></span>
              <div className="flex flex-wrap gap-2" data-testid="profile-dbs-status">
                {DBS_OPTIONS.map((opt) => (
                  <PillToggle key={opt} label={opt} active={profile.dbs_status === opt}
                              onClick={() => set("dbs_status")(opt)} testid={`profile-dbs-${opt}`} />
                ))}
              </div>
            </div>
            <div>
              <span className="text-sm font-medium text-ink flex items-center mb-2">Insurance status<RequiredMark missing={isRequiredMissing("Insurance status")} /></span>
              <div className="flex flex-wrap gap-2" data-testid="profile-insurance-status">
                {INSURANCE_OPTIONS.map((opt) => (
                  <PillToggle key={opt} label={opt} active={profile.insurance_status === opt}
                              onClick={() => set("insurance_status")(opt)} testid={`profile-insurance-${opt}`} />
                ))}
              </div>
            </div>
          </SectionCard>

          <SectionCard eyebrow="Contact" title="Contact preference">
            <div>
              <span className="text-sm font-medium text-ink block mb-2">Preferred contact method(s)</span>
              <div className="flex flex-wrap gap-2" data-testid="profile-contact-methods">
                {CONTACT_METHODS.map((opt) => (
                  <PillToggle key={opt} label={opt} active={(profile.preferred_contact_methods || []).includes(opt)}
                              onClick={() => toggleInArray("preferred_contact_methods", opt)} testid={`profile-contact-${opt}`} />
                ))}
              </div>
              <p className="mt-2 text-xs text-ink-muted">
                Never shown publicly — only revealed once a family and carer reach an accepted introduction.
              </p>
            </div>
          </SectionCard>

          <SectionCard eyebrow="Additional details" title="Qualifications, training & specialist experience">
                <label className="block">
                  <span className="text-sm font-medium text-ink">Qualifications & training</span>
                  <textarea value={profile.qualifications_training || ""} rows={3}
                            onChange={(e) => set("qualifications_training")(e.target.value)}
                            placeholder="e.g. NVQ Level 3 in Health & Social Care, Dementia specialist training"
                            className={inputCls} data-testid="profile-qualifications-training" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-ink">Additional specialisms</span>
                  <textarea value={profile.additional_specialisms || ""} rows={2}
                            onChange={(e) => set("additional_specialisms")(e.target.value)}
                            className={inputCls} data-testid="profile-additional-specialisms" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-ink">Professional background</span>
                  <textarea value={profile.professional_background || ""} rows={3}
                            onChange={(e) => set("professional_background")(e.target.value)}
                            placeholder="e.g. years of experience, previous agencies or sectors worked in"
                            className={inputCls} data-testid="profile-professional-background" />
                </label>
              </SectionCard>

              <SectionCard eyebrow="Additional details" title="Detailed working preferences & rates">
                <label className="block">
                  <span className="text-sm font-medium text-ink">Detailed working preferences</span>
                  <textarea value={profile.detailed_rota_preferences || ""} rows={3}
                            onChange={(e) => set("detailed_rota_preferences")(e.target.value)}
                            className={inputCls} data-testid="profile-detailed-rota-preferences" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-ink">Indicative rates</span>
                  <input type="text" value={profile.indicative_rates || ""}
                         onChange={(e) => set("indicative_rates")(e.target.value)}
                         placeholder="e.g. £20–£25 per hour, or £900–£1,100 per week for live-in"
                         className={inputCls} data-testid="profile-indicative-rates" />
                </label>
              </SectionCard>

              <SectionCard eyebrow="Additional details" title="Testimonials">
                <label className="block">
                  <span className="text-sm font-medium text-ink">Testimonials or feedback</span>
                  <textarea value={profile.testimonials || ""} rows={3}
                            onChange={(e) => set("testimonials")(e.target.value)}
                            className={inputCls} data-testid="profile-testimonials" />
                </label>
              </SectionCard>

              <SectionCard eyebrow="Additional details" title="Driving, languages & wider travel">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <span className="text-sm font-medium text-ink block mb-2">Driving licence</span>
                    <div className="flex gap-2" data-testid="profile-has-driving-licence">
                      <PillToggle label="Yes" active={profile.has_driving_licence === true} onClick={() => set("has_driving_licence")(true)} testid="profile-driving-licence-yes" />
                      <PillToggle label="No" active={profile.has_driving_licence === false} onClick={() => set("has_driving_licence")(false)} testid="profile-driving-licence-no" />
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-ink block mb-2">Own vehicle</span>
                    <div className="flex gap-2" data-testid="profile-has-vehicle">
                      <PillToggle label="Yes" active={profile.has_vehicle === true} onClick={() => set("has_vehicle")(true)} testid="profile-vehicle-yes" />
                      <PillToggle label="No" active={profile.has_vehicle === false} onClick={() => set("has_vehicle")(false)} testid="profile-vehicle-no" />
                    </div>
                  </div>
                </div>
                <label className="block">
                  <span className="text-sm font-medium text-ink">Languages spoken</span>
                  <input type="text" value={profile.languages_spoken || ""}
                         onChange={(e) => set("languages_spoken")(e.target.value)}
                         placeholder="e.g. English, Polish, Urdu"
                         className={inputCls} data-testid="profile-languages-spoken" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-ink">Wider travel information</span>
                  <input type="text" value={profile.travel_radius_detail || ""}
                         onChange={(e) => set("travel_radius_detail")(e.target.value)}
                         placeholder="e.g. Willing to travel up to 50 miles, or open to short placements nationwide"
                         className={inputCls} data-testid="profile-travel-radius-detail" />
                </label>
              </SectionCard>

              <SectionCard eyebrow="Additional details" title="Fuller DBS & insurance details">
                <div className="grid sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="text-sm font-medium text-ink">DBS certificate date</span>
                    <input type="date" value={profile.dbs_certificate_date || ""}
                           onChange={(e) => set("dbs_certificate_date")(e.target.value)}
                           className={inputCls} data-testid="profile-dbs-certificate-date" />
                  </label>
                  <div>
                    <span className="text-sm font-medium text-ink block mb-2">On DBS update service?</span>
                    <div className="flex flex-wrap gap-2" data-testid="profile-dbs-update-service">
                      {DBS_UPDATE_SERVICE_OPTIONS.map((opt) => (
                        <PillToggle key={opt} label={opt} active={profile.dbs_update_service === opt}
                                    onClick={() => set("dbs_update_service")(opt)} testid={`profile-dbs-update-${opt}`} />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="text-sm font-medium text-ink">Insurance type</span>
                    <input type="text" value={profile.insurance_type || ""}
                           onChange={(e) => set("insurance_type")(e.target.value)}
                           className={inputCls} data-testid="profile-insurance-type" />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-ink">Insurance renewal date</span>
                    <input type="date" value={profile.insurance_renewal_date || ""}
                           onChange={(e) => set("insurance_renewal_date")(e.target.value)}
                           className={inputCls} data-testid="profile-insurance-renewal-date" />
                  </label>
                </div>
              </SectionCard>

          <SectionCard eyebrow="Declaration" title="Accuracy declaration">
            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" checked={!!profile.declaration_consent}
                     onChange={(e) => set("declaration_consent")(e.target.checked)}
                     className="mt-1 w-4 h-4" data-testid="profile-declaration-consent" />
              <span className="flex items-center">I confirm the information provided is accurate.<RequiredMark missing={isRequiredMissing("Accuracy declaration")} /></span>
            </label>
          </SectionCard>

          <SectionCard eyebrow="Visibility" title="Show my profile in the Carer directory">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                {profile.publicly_visible !== false
                  ? <Eye className="w-5 h-5 text-teal mt-0.5 shrink-0" strokeWidth={1.75} />
                  : <EyeOff className="w-5 h-5 text-ink-muted mt-0.5 shrink-0" strokeWidth={1.75} />}
                <p className="text-sm text-ink-muted max-w-lg">
                  {profile.publicly_visible !== false
                    ? "Once approved, your profile can be seen by families browsing Carer Profiles."
                    : "Your profile is hidden — it won't appear to families even once approved. Your saved details and review history are kept exactly as they are."}
                </p>
              </div>
              <Switch
                checked={profile.publicly_visible !== false}
                onCheckedChange={toggleVisibility}
                disabled={visibilitySaving}
                data-testid="profile-visibility-toggle"
              />
            </div>
          </SectionCard>

          {profile.status === "approved" && (
            <SectionCard eyebrow="Public Sharing Link" title="Public Sharing Link">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Share2 className={`w-5 h-5 mt-0.5 shrink-0 ${profile.public_sharing_enabled ? "text-teal" : "text-ink-muted"}`} strokeWidth={1.75} />
                  <p className="text-sm text-ink-muted max-w-lg">
                    Allow people with your personal profile link to view a public version of your
                    profile without signing into JCC. Your email address, telephone number and
                    private account information will never be displayed. Visitors must join or log
                    into free Family Access before contacting you.
                  </p>
                </div>
                <Switch
                  checked={!!profile.public_sharing_enabled}
                  onCheckedChange={togglePublicSharing}
                  disabled={sharingSaving}
                  data-testid="profile-public-sharing-toggle"
                />
              </div>
              {profile.public_sharing_enabled && profile.public_profile_link && (
                <div className="mt-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="profile-public-sharing-link-row">
                  <span className="flex-1 text-sm text-ink truncate" data-testid="profile-public-sharing-link">
                    {profile.public_profile_link.startsWith("http") ? profile.public_profile_link : `${window.location.origin}${profile.public_profile_link}`}
                  </span>
                  <button
                    type="button"
                    onClick={copyShareLink}
                    className="btn-outline text-xs shrink-0"
                    data-testid="profile-public-sharing-copy-btn"
                  >
                    {linkCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {linkCopied ? "Copied" : "Copy link"}
                  </button>
                </div>
              )}
              {!profile.public_sharing_enabled && (
                <p className="text-xs text-ink-soft">Off by default — turning this on will email you your link straight away.</p>
              )}
            </SectionCard>
          )}

          <PrivacyReassuranceNote heading="Your information remains yours." testId="profile-privacy-note">
            JCC will never sell your personal information or use it for unrelated marketing. We only
            process it to provide and protect the JCC service. You control whether your approved
            profile is publicly visible and can switch public sharing off at any time.
          </PrivacyReassuranceNote>

          <div className="sticky bottom-4 rounded-2xl border border-slate-200 bg-white/95 backdrop-blur p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-ink-muted" data-testid="profile-save-status">
                {saving ? "Saving…" : dirty ? "Unsaved changes" : lastSaved ? `Saved at ${lastSaved.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : "Draft — not yet saved"}
              </span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={saveDraft} disabled={saving} className="btn-outline text-sm" data-testid="profile-save-draft-btn">
                  <Save className="w-4 h-4" /> Save draft
                </button>
                {profile.status !== "submitted" && (
                  <button
                    type="button"
                    onClick={submitForReview}
                    disabled={submitting || liveMissing.length > 0}
                    title={liveMissing.length > 0 ? "Complete the required fields listed above first" : undefined}
                    className="btn-purple text-sm"
                    data-testid="profile-submit-review-btn"
                  >
                    <Send className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit for review"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
